import React, { useState, createContext, useContext } from 'react';
interface ModuleAccess {
  production: boolean;
  laboratory: boolean;
  disposition: boolean;
}
interface User {
  id: number;
  username: string;
  password: string;
  isAdmin: boolean;
  moduleAccess: ModuleAccess;
}
interface UserContextType {
  user: User | null;
  users: User[];
  login: (username: string, password: string) => Promise<boolean>;
  adminLogin: (password: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (user: User) => void;
  createUser: (user: Omit<User, 'id'>) => void;
}
const INITIAL_USERS: User[] = [{
  id: 1,
  username: 'user1',
  password: 'pass1',
  isAdmin: false,
  moduleAccess: {
    production: true,
    laboratory: false,
    disposition: false
  }
}, {
  id: 2,
  username: 'user2',
  password: 'pass2',
  isAdmin: false,
  moduleAccess: {
    production: true,
    laboratory: true,
    disposition: false
  }
}, {
  id: 3,
  username: 'admin',
  password: 'admin123',
  isAdmin: true,
  moduleAccess: {
    production: true,
    laboratory: true,
    disposition: true
  }
}];
const ADMIN_PASSWORD = 'admin123';
const UserContext = createContext<UserContextType | undefined>(undefined);
export function UserProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>(INITIAL_USERS);
  const login = async (username: string, password: string) => {
    const user = users.find(u => u.username === username && u.password === password);
    if (user) {
      setUser(user);
      return true;
    }
    return false;
  };
  const adminLogin = async (password: string) => {
    if (password === ADMIN_PASSWORD) {
      return true;
    }
    return false;
  };
  const logout = () => {
    setUser(null);
  };
  const updateUser = (updatedUser: User) => {
    setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
  };
  const createUser = (newUser: Omit<User, 'id'>) => {
    const maxId = Math.max(...users.map(u => u.id));
    const userWithId = {
      ...newUser,
      id: maxId + 1
    };
    setUsers([...users, userWithId]);
  };
  return <UserContext.Provider value={{
    user,
    users,
    login,
    adminLogin,
    logout,
    updateUser,
    createUser
  }} data-id="element-1294">
      {children}
    </UserContext.Provider>;
}
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}